import { TContentDomain } from '@/types/Content.types';

// Mirrors the backend domain → subdomain map used to validate content at publish
// time (back-end/backend-monolith/src/events/types/domain-validators.ts, sourced
// from the subdomain enums in assessment/types/events.types.ts). Domain keys are
// the lowercase HealthDomain values; subdomain values are the exact enum strings
// the backend expects.

export const HEALTH_DOMAINS: TContentDomain[] = [
  'physical',
  'mental',
  'social',
  'vocational',
  'spiritual',
  'purpose',
  'fun',
];

export const HEALTH_DOMAIN_SUBDOMAINS: Record<TContentDomain, string[]> = {
  physical: [
    'PHYSICAL_ACTIVITY',
    'BODY_ACHES_OR_PAINS',
    'HEALTH_CHECKUPS',
    'SLEEP_PATTERNS',
    'EATING_HABITS',
    'ALCOHOL_AND_OTHER_DRUG_USE',
    'COMPULSIVE_BEHAVIORS_EXCLUDING_ALCOHOL_AND_DRUGS',
    'OTHER_PHYSICAL',
  ],
  mental: [
    'FEELING_SAD_OR_DOWN',
    'UNDERSTANDING_OTHERS_FEELINGS',
    'FEELING_NERVOUS_OR_WORRIED',
    'MANAGING_EMOTIONS',
    'TROUBLE_DOING_DAILY_ACTIVITIES',
    'FEELING_OVERWHELMED',
    'SUICIDE',
    'DEALING_WITH_TRAUMA',
    'OTHER_MENTAL',
  ],
  social: [
    'FEELING_CLOSE_TO_PEOPLE',
    'RELATIONSHIP_WITH_PARENTS_AND_SIBLINGS',
    'RELATIONSHIP_WITH_SPOUSE_OR_PARTNER',
    'RELATIONSHIP_WITH_CHILDREN',
    'COMMITMENT_TO_SPOUSE_OR_PARTNER',
    'FAMILY_TROUBLES_GROWING_UP',
    'WITHDRAWING_FROM_PEOPLE',
    'SUPPORT_FROM_FRIENDS_FAMILY_OR_COMMUNITY',
    'ANGER',
    'OTHER_SOCIAL',
  ],
  vocational: [
    'BALANCING_WORK_AND_PERSONAL_LIFE',
    'MANAGING_MONEY',
    'JOB_SATISFACTION',
    'RELATIONSHIP_WITH_EMPLOYER',
    'OTHER_VOCATIONAL_FINANCIAL',
  ],
  spiritual: [
    'DISCONNECTED_FROM_SPIRITUALITY',
    'LACK_OF_SPIRITUAL_DEVOTION',
    'STRUGGLING_WITH_FORGIVENESS',
    'LACK_OF_SPIRITUAL_SUPPORT',
    'OTHER_SPIRITUAL',
  ],
  purpose: [
    'LIFE_SATISFACTION',
    'DETERMINATION_AND_RESILIENCE',
    'STUCK_OR_UNMOTIVATED',
    'DIFFICULTY_FINDING_PURPOSE',
    'OTHER_PURPOSE_RESILIENCE',
  ],
  fun: [
    'LACK_OF_HOBBIES',
    'NO_TIME_TO_RELAX',
    'STRUGGLES_WITH_SELF_CARE',
    'BORED_WITH_ROUTINES',
    'OTHER_FUN_RELAXATION',
  ],
};

// 'PHYSICAL_ACTIVITY' -> 'Physical Activity'
export const formatSubdomainLabel = (subdomain: string): string =>
  subdomain
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// 'physical' -> 'Physical'
export const formatDomainLabel = (domain: string): string =>
  domain.charAt(0).toUpperCase() + domain.slice(1);

export const getSubdomainsForDomain = (domain?: TContentDomain | null): string[] =>
  domain ? (HEALTH_DOMAIN_SUBDOMAINS[domain] ?? []) : [];

// Subdomains grouped by their parent domain, for only the selected domains —
// used by the sectioned subdomain dropdown.
export const getSubdomainSectionsForDomains = (
  domains: TContentDomain[],
): Record<string, string[]> =>
  Object.fromEntries(domains.map((domain) => [domain, HEALTH_DOMAIN_SUBDOMAINS[domain] ?? []]));

// Union of valid subdomains across all selected domains (deduped, order preserved).
export const getSubdomainsForDomains = (domains: TContentDomain[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const domain of domains) {
    for (const subdomain of getSubdomainsForDomain(domain)) {
      if (!seen.has(subdomain)) {
        seen.add(subdomain);
        result.push(subdomain);
      }
    }
  }
  return result;
};
